import { AccountInfo } from '@azure/msal-browser';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { ROUTES } from 'routing';
import { useAuthState } from '../stores';
import { AppPermission, InsightB2CClaims, UserInfo } from '../types';
import { getAppPermissions } from '../utility';

const getDefaultUserInfoFromIdClaims = ({
    given_name,
    family_name,
    name,
    email,
    sub,
    InsightClaims,
}: Record<string, string>): UserInfo => ({
    firstName: given_name,
    lastName: family_name,
    email,
    displayName: name,
    id: sub,
    insightClaims: InsightClaims,
});

export interface UseHandleAccountProps {
    /**
     * The id of the client
     */
    clientId: string;
    /**
     * the permission key to login in the app
     */
    loginPermission: AppPermission;
}

/**
 *
 * @remarks
 * This hook is designed to check the user permissions to login and clear when signing out.
 *
 * @returns An object containing:
 * - A function to handle the user account and navigate to a unauthorized page if the user does not have permissions
 * - A function to clear the current logged in user.
 */
export function useHandleAccount({ loginPermission, clientId }: UseHandleAccountProps) {
    const state = useAuthState();
    const history = useHistory();
    const clearUser = () => state.user.set(null);

    const handleUserAccount = React.useCallback(
        (account: AccountInfo): void => {
            const setUser = (user: UserInfo) => state.user.set(user);
            if (account.idTokenClaims) {
                const defaultUser = getDefaultUserInfoFromIdClaims(account.idTokenClaims as InsightB2CClaims);
                const appPermissions = getAppPermissions(defaultUser.insightClaims);
                if (!appPermissions.includes(loginPermission)) {
                    history.push(ROUTES.unauthorized);
                }
                const resolvedUser = defaultUser;

                setUser(resolvedUser);
            }
        },
        [loginPermission, state.user, history]
    );

    return {
        handleUserAccount,
        clearUser,
    } as const;
}
